import { useMemo, useState } from "react";
import { MinusCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { ContainerCard, EmployeeDetails, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { SUBORDINATES_URL } from "../../constants/api";
import { useOrgAdminBranchColumn } from "../../hooks/useOrgAdminBranchColumn";

const EmployeeDeductions = () => {
    const navigate = useNavigate();
    const { showBranchColumn, insertBranchColumn, TempTransferBadge } = useOrgAdminBranchColumn("employee");

    const isAdmin =
        JSON.parse(sessionStorage.getItem("is_admin") || "false") ||
        JSON.parse(sessionStorage.getItem("is_super_admin") || "false");

    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [scope, setScope] = useState("all");

    const tableFilter = useMemo(
        () => (scope === "team" ? { scope: "team" } : {}),
        [scope],
    );

    const columns = useMemo(() => insertBranchColumn([
        {
            accessor: "employee",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            width: "250px",
            Cell: ({ row }) => {
                const record = row;
                const empDetails = {
                    ...record?.employee,
                    designation_id: record?.employee?.designation
                        ? {
                            id: record.employee.designation.id,
                            designation_name: record.employee.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.employee?.employee_name || "-",
                    id: record?.employee?.employee_id || "-",
                };
                return (
                    <div className="flex items-center gap-2">
                        <EmployeeDetails details={empDetails} size="xs" />
                        {record?.on_temp_transfer && !showBranchColumn && <TempTransferBadge />}
                    </div>
                );
            },
        },
        {
            accessor: "department",
            Header: "Department",
            filterable: true,
        },
        {
            accessor: "date_of_joining",
            Header: "Date of joining",
            filterable: true,
            filterType: "date",
            Cell: ({ value }) => (value ? dayjs(value, "YYYY-MM-DD").format("DD-MM-YYYY") : "-"),
        },
        {
            accessor: "actions",
            Header: "Actions",
            fixed: true,
            align: "right",
            Cell: ({ row }) => (
                <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1 text-rose-600"
                    onClick={() =>
                        navigate("/employee/deductions/manage", {
                            state: {
                                employeeID: row.employee?.id,
                                employeeName: row.employee?.employee_name,
                            },
                        })
                    }
                >
                    <MinusCircle className="h-4 w-4" />
                    Manage Deductions
                </Button>
            ),
        },
    ]), [insertBranchColumn, navigate, showBranchColumn, TempTransferBadge]);

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={MinusCircle}
                title="Employee Deductions"
                subtitle="Assign and manage monthly variable deductions and fines for your team"
                totalRecords={totalRecords}
            />

            {isAdmin && (
                <div className="flex gap-2 mb-4">
                    <Button
                        variant={scope === "all" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setScope("all")}
                    >
                        All Employees
                    </Button>
                    <Button
                        variant={scope === "team" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setScope("team")}
                    >
                        My Team
                    </Button>
                </div>
            )}

            <Reactable
                columns={columns}
                data={data}
                setData={setData}
                setTotalCount={setTotalRecords}
                url={SUBORDINATES_URL}
                serverSide
                rowSize="md"
                filter={tableFilter}
                printFilter={tableFilter}
            />
        </ContainerCard>
    );
};

export default EmployeeDeductions;
